import React from 'react';
import { normalizeFeatureValue } from '../../utils/featureNormalization';
import { sortFeatureValues } from '../../utils/sortingUtils';

/**
 * RadioInput - Radio button group input
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {*} props.value - Current selected value
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.className - Additional CSS classes
 */
const RadioInput = ({ feature, value, onChange, disabled = false, isOptionDisabled, className = '' }) => {
    const values = sortFeatureValues((feature?.values || []).map(normalizeFeatureValue));
    const currentValue = value === undefined || value === null ? '' : String(value);
    
    const handleChange = (selectedValue) => {
        onChange({ target: { value: String(selectedValue ?? '') } });
    };
    
    if (values.length === 0) {
        return (
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                No options available
            </div>
        );
    }
    
    return (
        <div className="radio-input-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {values.map((val) => {
                const valKey = String(val.value ?? '');
                const display = val.displayText || val.value;
                const isSelected = valKey === currentValue;
                const isValueDisabled = disabled || Boolean(isOptionDisabled?.(valKey));
                
                return (
                    <label 
                        key={val.id || valKey}
                        className={className}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: isValueDisabled ? 'not-allowed' : 'pointer',
                            opacity: isValueDisabled ? 0.6 : 1,
                            backgroundColor: isSelected ? 'rgba(0, 30, 98, 0.05)' : undefined,
                            transition: 'background-color 0.15s',
                            '&:hover': { backgroundColor: 'var(--bg-secondary)' }
                        }}
                        onMouseEnter={(e) => {
                            if (!isValueDisabled) {
                                e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isValueDisabled && !isSelected) {
                                e.currentTarget.style.backgroundColor = '';
                            }
                        }}
                    >
                        <input
                            type="radio"
                            name={feature?.name || 'radio-group'}
                            value={valKey}
                            checked={isSelected}
                            onChange={(e) => {
                                if (!isValueDisabled && e.target.checked) {
                                    handleChange(e.target.value);
                                }
                            }}
                            disabled={isValueDisabled}
                            style={{
                                width: '18px',
                                height: '18px',
                                accentColor: 'var(--accent)',
                                cursor: isValueDisabled ? 'not-allowed' : 'pointer'
                            }}
                        />
                        <span style={{ fontSize: '0.9rem' }}>{display}</span>
                        {val.description && (
                            <span 
                                style={{
                                    fontSize: '0.75rem',
                                    color: 'var(--text-secondary)',
                                    fontStyle: 'italic'
                                }}
                            >
                                - {val.description}
                            </span>
                        )}
                    </label>
                );
            })}
        </div>
    );
};

export default RadioInput;
